import {
  Button,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Link } from "react-router";
import {
  LabellingCoverageGranularity,
  useGetTenantLabellingCoverageQuery,
} from "store/api/generatedApi";
import { CoverageSection } from "./CoverageSection";
import { useTenantTranslation } from "hooks/formatters";

interface TenantCoverageCardProps {
  tenantName: string;
  startDate: string;
  endDate: string;
  granularity: LabellingCoverageGranularity;
  gapTolerance: number;
}

export const TenantCoverageCard = ({
  tenantName,
  startDate,
  endDate,
  granularity,
  gapTolerance,
}: TenantCoverageCardProps) => {
  const { t } = useTenantTranslation();

  const { data, isLoading, isError } = useGetTenantLabellingCoverageQuery({
    tenantName,
    start: startDate,
    end: endDate,
    granularity,
    gapToleranceSeconds: gapTolerance,
  });

  if (isLoading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2} alignItems="center">
          <Typography variant="h6">{tenantName}</Typography>
          <CircularProgress size={32} />
        </Stack>
      </Paper>
    );
  }

  if (isError || !data) {
    return (
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Typography variant="h6">{tenantName}</Typography>
          <Box
            sx={{
              py: 4,
              px: 2,
              textAlign: "center",
              bgcolor: "error.lighter",
              borderRadius: 1,
            }}
          >
            <Typography
              color="error"
              variant="body2"
              sx={{ wordWrap: "break-word", overflowWrap: "break-word" }}
            >
              {t("errorLoadingData")}
            </Typography>
          </Box>
        </Stack>
      </Paper>
    );
  }

  return (
    <CoverageSection
      title={tenantName}
      buckets={data.buckets}
      summary={data.summary}
      headerAction={
        <Button
          component={Link}
          to={tenantName}
          variant="outlined"
          size="small"
        >
          {t("openLink")}
        </Button>
      }
    />
  );
};
